import { bytesToNDigits } from '@mail/cross-sizes-utils';
import {
    type BillingDataIos,
    type BillingDataResponse,
    type BillingDataReward,
    type BillingDataWeb,
    type BillingDataWebGraceHistory,
    type BillingDataWebGraceInterval,
    BillingDataPartner,
    BillingDataPeriod,
    BillingDataPlatform,
    BillingDataStatus,
} from 'reactApp/api/billing/data/BillingDataAPICall.types';
import { generateSubscriptionId } from 'reactApp/utils/generateSubscriptionId';
import { bytesToGB } from 'reactApp/utils/sizeHelper';

import { PARTNER_LIST } from './billing.data';
import { getBillingDataLinks } from './billingData.links';
import {
    type BillingDataGrace,
    type BillingDataReceivedGiftsRecord,
    type BillingDataSentGift,
    type BillingDataSentGiftsRecord,
    type BillingDataSubscriptionInfo,
    type BillingDataSubscriptionRecord,
    type BillingPaidInfo,
    type BillingReferralProgram,
    BillingDataSubscriptionInfoStatus,
} from './billingData.types';

const STATUS_MAP = {
    A: BillingDataSubscriptionInfoStatus.ACTIVE,
    F: BillingDataSubscriptionInfoStatus.FINISHED,
    S: BillingDataSubscriptionInfoStatus.SUSPENDED,
    G: BillingDataSubscriptionInfoStatus.GRACE,
    H: BillingDataSubscriptionInfoStatus.HOLD, // Пока нет
    L: BillingDataSubscriptionInfoStatus.LAPSED,
    W: BillingDataSubscriptionInfoStatus.WAITING,
    R: BillingDataSubscriptionInfoStatus.RENEW,
} as const;

const getStatus = (status: BillingDataStatus, autorenewal?: boolean): BillingDataSubscriptionInfoStatus => {
    if (status === BillingDataStatus.ACTIVE && autorenewal === false) {
        return BillingDataSubscriptionInfoStatus.SUSPENDED;
    }
    return STATUS_MAP[status];
};

const getRenewAt = (renewNext?: number) => {
    return typeof renewNext === 'undefined' ? undefined : calculateDate(renewNext);
};

const SUBSCRIPTION_TYPE_LIST = [
    BillingDataPlatform.web,
    BillingDataPlatform.ios,
    BillingDataPlatform.android,
    BillingDataPlatform.reward,
    BillingDataPlatform.rustore,
] as const;

const calculateDate = (date: number) => date && date * 1000;

function hasSubscriptionId(item: unknown): item is BillingDataWeb {
    return !!item && typeof item === 'object' && 'sub_id' in item;
}

function hasRewardId(item: unknown): item is BillingDataReward {
    return !!item && typeof item === 'object' && 'reward_id' in item;
}

function mapCommon(subscription: BillingDataWeb | BillingDataIos | BillingDataReward, type: BillingDataPlatform) {
    const expires = calculateDate(subscription.finish);
    const quota = subscription?.services?.cloud?.quota ?? 0;
    const { common } = subscription.services || ({} as any);
    const space = bytesToNDigits(quota, 3);
    const flags = subscription.services.cloud?.flags;
    return {
        expires,
        quota,
        type,
        productId: subscription.product,
        space,
        flags: {
            upload: !!flags?.PAID_UPLOAD,
            paidFeatures: !!flags?.PAID_ACCOUNT,
            pro: !!flags?.PRO,
        },
        /* В старой ручке смотрели на признак services.mailru, но это признак единоквотной подписки,
        сейчас все подписки единоквотные
         */
        isMail: !!flags?.PAID_UPLOAD && !!common?.disable_ads,
        disableAds: common?.disable_ads,
    } as const;
}

export function mapSubscription(subscription: BillingDataWeb, type: BillingDataPlatform, start: number): BillingDataSubscriptionRecord {
    const { expires, quota, ...rest } = mapCommon(subscription, type);
    const { icon } = getBillingDataLinks(type);
    const { autorenewable, renew_next, amounts, intervals, renewable = false } = subscription;
    const status = getStatus(subscription.status, autorenewable);
    const id = generateSubscriptionId({
        period: subscription.period,
        subId: subscription.sub_id,
        start,
        quota,
        type,
        expires,
    }).toString() as string;
    return {
        [id]: {
            ...rest,
            id,
            start,
            expires,
            icon,
            status,
            autorenewal: autorenewable,
            renewalCost: amounts?.renewal,
            purchaseCost: amounts?.purchase,
            discountCost: amounts?.discount,
            regularCost: amounts?.regular,
            productPeriod: intervals?.paid,
            productDiscountPeriod: intervals?.discount,
            productTrialPeriod: intervals?.trial || intervals?.free_start,
            productFreemiumPeriod: intervals?.free_start,
            renewAt: getRenewAt(renew_next),
            renewable,
            isTrial: subscription.period === BillingDataPeriod.TRIAL || subscription.period === BillingDataPeriod.FREE_START,
            isFreemium: subscription.period === BillingDataPeriod.FREE_START,
            isDiscount: subscription.period === BillingDataPeriod.DISCOUNT,
            isPrepaid: subscription.prepaid,
            currentPeriod: subscription.period,
            grace: mapGrace(subscription),
        },
    };
}

const supportedPlatforms = [
    BillingDataPartner.mail,
    BillingDataPlatform.android,
    BillingDataPlatform.base,
    BillingDataPlatform.huawei,
    BillingDataPlatform.ios,
    BillingDataPlatform.reward,
    BillingDataPlatform.web,
    BillingDataPlatform.rustore,
    // BillingDataPartner.vkmusic, TODO: https://jira.vk.team/browse/CLOUDWEB-18924 - не забыть поддержать, когда будем переводить промки на билингдату
];

function mapReward(subscription: BillingDataReward, type: BillingDataPlatform, start: number): BillingDataSubscriptionRecord {
    const { expires, quota, ...rest } = mapCommon(subscription, type);
    const partner = subscription.platform;
    /** TODO
     * Удалить блок с if после полного перехода на billingData;
     * Старая ручка subscriptions/list возвращала в ревардах только подписки с партнером ru.mail.special;
     * Поэтому для совместимости с другим кодом, ожидающим такое поведение добавил сюда проверку
     * Познее, когда billind/data будет использоваться и для получения promo, if нужно будет убрать,
     * с изменением соотвествующей логики в других местах;
     *
     * Касаемо платформ в списке BillingDataPlatform supportedPlatforms, это в связи с тем, что реварды и
     * платформ-подписки это одна сущность и тоже добавлено для обратной совсестимости с promo
     * */
    const { platform } = subscription;

    if (!supportedPlatforms.includes(platform)) {
        return {};
    }

    const { icon, accountLink } = getBillingDataLinks(type, platform);

    const status = getStatus(subscription.status);
    const id = generateSubscriptionId({
        subId: subscription.reward_id,
        start,
        quota,
        type,
        expires,
    }).toString() as string;
    return {
        [id]: {
            ...rest,
            id,
            start,
            expires,
            partner: PARTNER_LIST.includes(partner) ? partner : undefined,
            icon,
            accountLink,
            status,
            renewAt: getRenewAt(),
            renewable: false,
            isTrial: false,
            isFreemium: false,
            isDiscount: false,
            isPrepaid: false,
        },
    };
}

type BillingDataUnion = BillingDataWeb | BillingDataIos | BillingDataReward;

export function mapBillingDataSubscription(billingData: BillingDataResponse): BillingDataSubscriptionRecord {
    const today = Date.now();
    return SUBSCRIPTION_TYPE_LIST.reduce((subscriptionsAcc, type) => {
        const billingDataByType = billingData?.data?.[type];
        if (!billingDataByType || !billingDataByType.length) {
            return subscriptionsAcc;
        }

        const subscriptionsByType = (billingDataByType as BillingDataUnion[]).reduce((subscriptionByTypeAcc, subscription) => {
            const start = calculateDate(subscription.start);

            if (start && today < start) {
                return subscriptionByTypeAcc;
            }
            if (hasSubscriptionId(subscription)) {
                return { ...subscriptionByTypeAcc, ...mapSubscription(subscription, type, start) };
            }
            if (hasRewardId(subscription)) {
                return { ...subscriptionByTypeAcc, ...mapReward(subscription, type, start) };
            }
            return subscriptionByTypeAcc;
        }, {} as BillingDataSubscriptionRecord);
        return { ...subscriptionsAcc, ...subscriptionsByType };
    }, {} as BillingDataSubscriptionRecord);
}

export const normalizeBillingPaidInfo = (billingData: BillingDataResponse): BillingPaidInfo => {
    const cloudPaidInfo = billingData?.paid_info?.cloud;

    let trialExpires = 0;
    let paidExpires = 0;

    if (cloudPaidInfo) {
        Object.keys(cloudPaidInfo).forEach((platform) => {
            const trial = calculateDate(cloudPaidInfo[platform].trial);
            const paid = calculateDate(cloudPaidInfo[platform].paid);

            paidExpires = Math.max(paid, paidExpires);
            trialExpires = Math.max(trial, trialExpires);
        });
    }

    return {
        trialExpires,
        paidExpires,
    };
};

export function mapReceivedGifts(billingData: BillingDataResponse): BillingDataReceivedGiftsRecord {
    const today = Date.now();
    return billingData.data.gift_received.reduce((acc, gift) => {
        const discount = gift.giftible.services.cloud?.unlocked;
        const productId = discount?.web;
        const quota = gift.giftible.services.cloud?.quota || 0;
        const start = calculateDate(gift.start);

        if (start && today < start) {
            return acc;
        }

        return {
            ...acc,
            [gift.gift_id]: {
                expires: calculateDate(gift.finish),
                id: gift.giftible.product,
                start,
                created: calculateDate(gift.create),
                productId,
                discount,
                space: bytesToNDigits(quota, 3),
            },
        };
    }, {});
}

export function mapSentGifts(billingData: BillingDataResponse): BillingDataSentGiftsRecord {
    const { gift_sent } = billingData.data;
    return gift_sent
        .filter((gift) => !gift.giftible.services.cloud?.unlocked)
        .reduce((acc, gift) => {
            const quota = gift.giftible.services.cloud?.quota || 0;
            return {
                ...acc,
                [gift.code]: {
                    isGifted: gift.given,
                    created: calculateDate(gift.create),
                    id: gift.giftible.product,
                    code: gift.code,
                    giftId: gift.gift_id,
                    expires: calculateDate(gift.finish),
                    start: calculateDate(gift.start),
                    space: bytesToNDigits(quota, 3),
                } as BillingDataSentGift,
            };
        }, {});
}

export function mapReferralProgram(billingData: BillingDataResponse): BillingReferralProgram[] {
    const { referral = [] } = billingData.data;
    return referral.map(({ autorenewable = false, finish = 0, start = 0, referral = 'a@avatar', referral_id, type, services = {} }) => {
        return {
            id: referral_id,
            autorenewable,
            expires: calculateDate(finish),
            start: calculateDate(start),
            owner: { email: referral },
            type,
            quota: bytesToGB(services.cloud?.quota || 0),
        };
    });
}

// В луа могут забыть добавить замену {} на [], подстраховка тут
const fixLuaArrays = (items) => (Array.isArray(items) ? items : []);

export function mapGrace(subscription: BillingDataWeb): BillingDataGrace | undefined {
    const { grace } = subscription;
    if (!grace) {
        return;
    }

    const result: BillingDataGrace = { active: !!grace.active };
    const { partial, partial_ll } = grace;

    const mapHistory = (item: BillingDataWebGraceHistory) => ({
        ...item,
        date: calculateDate(item.date),
    });

    const mapIntervals = (item: BillingDataWebGraceInterval) => ({
        date: calculateDate(item.date),
        lastInterval: item.last_interval,
        futureInterval: item.future_interval,
    });

    if (partial) {
        const { history = [], cummulative_total, ...rest } = partial;
        result.partial = {
            ...rest,
            cummulativeTotal: cummulative_total,
            history: fixLuaArrays(history).map(mapHistory),
        };
    }
    if (partial_ll) {
        const { history = [], intervals = [], cummulative_total, last_interval, future_interval, ...rest } = partial_ll;
        result.partialLongLiving = {
            ...rest,
            cummulativeTotal: cummulative_total,
            lastInterval: last_interval,
            futureInterval: future_interval,
            history: fixLuaArrays(history).map(mapHistory),
            intervals: fixLuaArrays(intervals).map(mapIntervals),
        };
    }
    return result;
}

export function mapSsrSubscription(data: BillingDataWeb[] = []): BillingDataSubscriptionInfo[] {
    const subscriptionRecords = data.reduce((acc, subscription) => {
        const start = calculateDate(subscription.start);

        return mapSubscription(subscription, BillingDataPlatform.web, start);
    }, {});

    return Object.values(subscriptionRecords);
}

export function getLatestExpiredSubscription(subscriptions: BillingDataSubscriptionInfo[]): BillingDataSubscriptionInfo | undefined {
    const sortedSubs = subscriptions.sort((sub1, sub2) => sub1.expires - sub2.expires);
    return sortedSubs[sortedSubs.length - 1];
}
